/* Copyright (c) 2020-present CloudIO Inc */

import { useStateXSelector } from '@cloudio-saas/statex';
import { makeStyles } from '@mui/styles';
import clsx from 'clsx';
import { BooleanProp } from 'componentregistry';
import { helpTextSelector } from 'designer/state';
import Icon from 'icons/Icon';
import React, { memo } from 'react';
import clickOnEnterOrSpace from 'utils/clickOnEnterOrSpace';
import { Box, Theme } from '@mui/material';
import { getDefaultValues } from './PropertyEditorUtils';
import { PropertyEditorProps } from './types';

const useStyles = makeStyles((theme: Theme) => ({
  root: {
    display: 'flex',
    alignItems: 'center',
    marginTop: 8,
  },
  inner: {
    display: 'flex',
    alignItems: 'center',
    backgroundColor: theme.palette.background.default,
    borderRadius: 4,
    position: 'relative',
    width: 120,
  },
  label: {
    flex: 1,
    minWidth: 80,
    color: theme.palette.text.secondary,
    fontSize: '0.8rem',
  },
  option: {
    alignSelf: 'center',
    border: '1px solid transparent',
    color: theme.palette.text.secondary,
    cursor: 'pointer',
    flex: 1,
    fontSize: '0.8rem',
    outline: 'none',
    overflow: 'hidden',
    padding: '4px 0',
    position: 'relative',
    textAlign: 'center',
    textOverflow: 'ellipsis',
    transition: 'color 0.2s',
    '& > svg': {
      height: '1em',
      width: '1em',
    },
    '&:not(:last-child)::after': {
      content: '""',
      position: 'absolute',
      top: 8,
      bottom: 8,
      right: 0,
      width: 1,
      backgroundColor: theme.palette.divider,
    },
    '&:not(:first-of-type)': {
      marginLeft: '-1px',
      borderTopLeftRadius: 0,
      borderBottomLeftRadius: 0,
    },
  },
  selected: {
    color: theme.palette.primary.main,
  },
  s: {
    backgroundColor: theme.palette.background.paper,
    position: 'absolute',
    top: 4,
    bottom: 4,
    borderRadius: 4,
    margin: '0 4px',
    boxShadow: 'rgb(0 0 0 / 25%) 0px 1px 6px 0px',
    transition: 'left 0.2s cubic-bezier(0.2,0,0,1)',
  },
  tip: {
    marginLeft: 8,
  },
  disabled: {
    pointerEvents: 'none',
    opacity: 0.4,
  },
}));

function Option({
  label,
  selected,
  onSelect,
  isViewOnlyUser = false,
}: {
  label: string;
  selected?: boolean;
  onSelect: () => void;
  isViewOnlyUser: boolean;
}) {
  const classes = useStyles();

  return (
    <div
      onKeyDown={(e) => clickOnEnterOrSpace(e, onSelect)}
      role="button"
      tabIndex={0}
      onClick={!isViewOnlyUser ? onSelect : () => {}}
      className={clsx(
        classes.option,
        selected ? classes.selected : null,
        isViewOnlyUser ? classes.disabled : null,
      )}
    >
      {label}
    </div>
  );
}

function OnOffProperty({
  appUid,
  activeItemId,
  def,
  pageId,
  property,
  mgr,
  value: val,
  onChange,
  isViewOnlyUser = false,
}: PropertyEditorProps<BooleanProp, boolean>) {
  const classes = useStyles();
  const helpText = useStateXSelector(helpTextSelector, {
    params: { appUid, pageId, itemId: activeItemId, property, type: mgr.type },
  });

  const value = val ?? (getDefaultValues(mgr, property) as boolean);
  return (
    <div data-testid={property} className={classes.root}>
      <div className={classes.label}>{def.label}</div>
      <div className={classes.inner}>
        <Box
          sx={{
            width: 'calc(50% - 8px)',
            left: value ? 0 : '50%',
          }}
          className={classes.s}
        />
        <Option
          isViewOnlyUser={isViewOnlyUser}
          label="On"
          onSelect={() => {
            onChange(property, true);
          }}
          selected={value}
        />
        <Option
          isViewOnlyUser={isViewOnlyUser}
          label="Off"
          onSelect={() => {
            onChange(property, false);
          }}
          selected={value === false}
        />
      </div>
      {helpText ? (
        <Icon
          className={classes.tip}
          data-tip-at="top"
          data-tip-delay={0}
          data-tip={helpText}
          iconName="comment-exclamation"
        />
      ) : null}
    </div>
  );
}

export default memo(OnOffProperty);
